import React, { useState, useRef, useEffect } from "react";
import { useClerk } from "@clerk/clerk-react";
import "./UserMenu.css";

const UserMenu = ({ user, onLogout, onOpenSettings }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef(null);
  const { openUserProfile } = useClerk();

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, []);

  // Get initials from name or email
  const getInitials = () => {
    if (user?.name) {
      const parts = user.name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    if (user?.email) {
      return user.email.substring(0, 2).toUpperCase();
    }
    return "??";
  };

  // Get display name
  const getDisplayName = () => {
    return user?.name || user?.email?.split("@")[0] || "User";
  };

  // Get user tier display
  const getUserTier = () => {
    return user?.tier || "Free";
  };

  // Get tier badge color
  const getTierColor = () => {
    const tier = getUserTier().toLowerCase();
    if (tier === "pro" || tier === "premium") return "#00ff88";
    if (tier === "team" || tier === "business") return "#007AFF";
    if (tier === "enterprise") return "#f59e0b";
    return "#64748b"; // Free tier
  };

  // Handle manage account click - opens Clerk's UserProfile modal
  const handleManageAccount = () => {
    setIsOpen(false);
    openUserProfile();
  };

  return (
    <div className="user-menu-container" ref={menuRef}>
      <button
        className="user-menu-trigger"
        onClick={() => setIsOpen(!isOpen)}
        title={`Signed in as ${getDisplayName()}`}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {user?.imageUrl && user?.hasImage ? (
          <img
            src={user.imageUrl}
            alt={getDisplayName()}
            className="user-avatar"
          />
        ) : (
          <div className="user-avatar-initials">{getInitials()}</div>
        )}
      </button>

      {isOpen && (
        <div className="user-menu-dropdown">
          {/* User Info Section */}
          <div className="user-menu-header">
            <div className="user-menu-avatar-large">
              {user?.imageUrl && user?.hasImage ? (
                <img src={user.imageUrl} alt={getDisplayName()} />
              ) : (
                <div className="user-avatar-initials large">
                  {getInitials()}
                </div>
              )}
            </div>
            <div className="user-menu-info">
              <span className="user-menu-name">{getDisplayName()}</span>
              <span className="user-menu-email">{user?.email}</span>
              {user?.company && (
                <span className="user-menu-company">{user.company}</span>
              )}
            </div>
          </div>

          {/* Tier Badge */}
          <div className="user-menu-tier">
            <span
              className="tier-badge"
              style={{ borderColor: getTierColor(), color: getTierColor() }}
            >
              {getUserTier()} Plan
            </span>
          </div>

          {/* Divider */}
          <div className="user-menu-divider" />

          {/* Actions */}
          <div className="user-menu-actions">
            {onOpenSettings && (
              <button
                className="user-menu-action"
                onClick={() => {
                  setIsOpen(false);
                  onOpenSettings();
                }}
              >
                <span className="action-icon">⚙️</span>
                <span>Settings</span>
              </button>
            )}
            <button className="user-menu-action" onClick={handleManageAccount}>
              <span className="action-icon">👤</span>
              <span>Manage Account</span>
            </button>
            {/* Support — same mailto pattern as the portal account page */}
            <button
              className="user-menu-action"
              onClick={() => {
                setIsOpen(false);
                window.open(
                  "mailto:support@osoptimus.com?subject=Optimus%20OS%20—%20question%20%2F%20feedback%20%2F%20problem",
                  "_blank",
                );
              }}
            >
              <span className="action-icon">💬</span>
              <span>Support</span>
            </button>
            <button className="user-menu-action logout" onClick={onLogout}>
              <span className="action-icon">🚪</span>
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserMenu;
